import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

export type PaymentScheduleStatus =
  | 'DELAYED'
  | 'PAID'
  | 'CANCELLED'
  | 'WRITTEN_OFF'
  | undefined;

export type PaymentSchedule = {
  invoicedInitialCommissionAmount: number;
  invoicedInterestAmount: number;
  items: Array<{
    commissionAmount: number;
    discountAmount: number;
    dueDate: string;
    initialCommissionAmount: number;
    initialCommissionDiscountAmount: number;
    interestAmount: number;
    interestDiscountAmount: number;
    invoicedCommissionAmount: number;
    invoicedInitialCommissionAmount: number;
    invoicedInterestAmount: number;
    invoicedPrincipalAmount: number;
    number: number;
    penaltyAmount: number;
    principalAmount: number;
    remainingPrincipal: number;
    startDate: string;
    totalAmount: number;
    totalDiscountAmount: number;
    status?: PaymentScheduleStatus;
  }>;
  // keys which we are adding on the FE
  activeInstalement: number;
  instalmentAmount: number;
};

type PaymentScheduleContextType = {
  paymentSchedule?: PaymentSchedule | null;
  fetchPaymentSchedule: () => Promise<PaymentSchedule | void>;
};

function getActiveInstalment(array?: any[]) {
  if (!array) {
    return null;
  }

  const activeInstalementItem = array.find(
    (item) => !item.status || item.status === 'DELAYED',
  );

  if (activeInstalementItem) {
    return activeInstalementItem.number;
  }

  return null;
}

const PaymentScheduleContext = createContext<PaymentScheduleContextType>(
  {} as PaymentScheduleContextType,
);

const usePaymentSchedule = (): PaymentScheduleContextType =>
  useContext(PaymentScheduleContext);

const PaymentScheduleProvider = ({ children }: Props): JSX.Element => {
  const [paymentSchedule, setPaymentSchedule] =
    useState<PaymentSchedule | null>();
  const { showError } = useError();
  const location = useLocation();

  const fetchPaymentSchedule =
    useCallback(async (): Promise<PaymentSchedule | void> => {
      try {
        const { data: fetchedPaymentSchedule } = await axiosInstance.get(
          '/client/loans/latest/payment-schedule',
        );

        if (fetchedPaymentSchedule) {
          fetchedPaymentSchedule.activeInstalement = getActiveInstalment(
            fetchedPaymentSchedule.items,
          );
          fetchedPaymentSchedule.instalmentAmount =
            fetchedPaymentSchedule.items.length;
        }

        setPaymentSchedule(fetchedPaymentSchedule);
        return fetchedPaymentSchedule;
      } catch (e) {
        setPaymentSchedule(null);
      }
    }, [showError]);

  useEffect(() => {
    const prefetch = async () => {
      if (paymentSchedule === undefined && isLogged()) {
        try {
          await fetchPaymentSchedule();
        } catch {}
      }
    };

    prefetch();
  }, [location]);

  const paymentScheduleContextValue = useMemo(
    () => ({
      paymentSchedule,
      fetchPaymentSchedule,
    }),
    [paymentSchedule, fetchPaymentSchedule],
  );

  return (
    <PaymentScheduleContext.Provider value={paymentScheduleContextValue}>
      {children}
    </PaymentScheduleContext.Provider>
  );
};

export { usePaymentSchedule as default, PaymentScheduleProvider };
